import React, { useEffect, useState } from 'react';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { useInvoicePdf } from './hooks/useBilling';
import styles from './InvoiceModal.module.css';
import { BillingInvoice } from './types';

interface InvoiceModalProps {
  invoice: BillingInvoice;
  canViewOutletInfo?: boolean;
  onClose: () => void;
}

const InvoiceModal: React.FC<InvoiceModalProps> = ({ invoice, canViewOutletInfo = false, onClose }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [error, setError] = useState('');

  const { fetchInvoicePdf, getInvoicePdf } = useInvoicePdf();
  const invoiceName = invoice?.invoiceName || 'Invoice';

  useEffect(() => {
    let isMounted = true;

    const loadPdf = async () => {
      if (!invoice) return;
      setIsLoading(true);
      setError('');

      if (!invoice.invoiceName) {
        setError('Invoice file not found');
        setIsLoading(false);
        return;
      }

      const blob = await fetchInvoicePdf(invoice.invoiceName);

      if (!isMounted) return;

      if (blob) {
        const objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
      } else {
        setError('Failed to load invoice preview');
      }
      setIsLoading(false);
    };

    loadPdf();

    return () => {
      isMounted = false;
    };
  }, [invoice, fetchInvoicePdf]);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const handleRetry = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl('');
    }
    setError('');
    setIsLoading(true);
    // Re-trigger effect by unmounting/remounting or just calling loadPdf again?
    // Since effect depends on fetchInvoicePdf which is stable, we can just call logic again or force re-render.
    // Simplest is to just call the logic again if we extracted it, but here we can just toggle a retry state or similar.
    // Actually, let's just call fetchInvoicePdf directly here.

    fetchInvoicePdf(invoice.invoiceName || '').then((blob) => {
      if (blob) {
        const objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
        setError('');
      } else {
        setError('Failed to load invoice preview');
      }
      setIsLoading(false);
    });
  };

  const handleDownload = async () => {
    if (!invoice.invoiceName) return;
    setIsDownloading(true);
    await getInvoicePdf(invoice.invoiceName, 'download');
    setIsDownloading(false);
  };

  // Handle click outside modal
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!invoice) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick} data-print-modal="true" data-testid="invoice-modal-overlay">
      <div className={styles.modalContent} data-testid="invoice-modal-content">
        <div className={styles.modalHeader}>
          <div className={styles.modalTitleGroup}>
            <h2>{invoiceName}</h2>
            <p className={styles.modalSubtitle}>Preview invoice details and download the PDF.</p>
          </div>
          <div className={styles.actions}>
            <button
              onClick={handleDownload}
              className={styles.downloadButton}
              disabled={isLoading || isDownloading}
              data-testid="download-invoice-button"
            >
              {isDownloading ? 'Preparing...' : 'Download PDF'}
            </button>
            <button onClick={onClose} className={styles.closeButton} data-testid="modal-close-button">
              ×
            </button>
          </div>
        </div>

        <div className={styles.modalBody} data-print-content="true">
          <div className={styles.viewerContainer}>
            {isLoading ? (
              <div className={styles.stateContainer}>
                <LoadingSpinner />
                <span className={styles.stateText}>Loading invoice preview…</span>
              </div>
            ) : error ? (
              <div className={styles.stateContainer}>
                  <p className={styles.errorMessage} data-testid="invoice-error-message">{error}</p>
                  <button className={styles.secondaryButton} onClick={handleRetry} data-testid="retry-button">
                  Try Again
                </button>
              </div>
            ) : pdfUrl ? (
                  <iframe src={pdfUrl} title={`Invoice ${invoiceName}`} className={styles.pdfViewer} data-testid="pdf-viewer" />
            ) : (
              <div className={styles.stateContainer}>
                <p className={styles.errorMessage}>Invoice preview is unavailable.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceModal;
